// "Did you mean …@gmail.com?" for an email address typed on a form.
//
// Parents mistyping their address at registration was the main cause of
// bounced concussion updates. This catches the common slips in the part
// after the @ (gmial.com, hotmial.co.uk, outlook.con) for the providers UK
// families mostly use, and suggests the fix for them to accept. A school
// or work address that isn't close to one of these is left alone.

export const COMMON_EMAIL_DOMAINS = [
  "gmail.com", "googlemail.com",
  "hotmail.com", "hotmail.co.uk", "outlook.com", "outlook.co.uk", "live.com", "live.co.uk", "msn.com",
  "icloud.com", "me.com", "mac.com",
  "yahoo.com", "yahoo.co.uk", "ymail.com",
  "btinternet.com", "btopenworld.com", "sky.com", "virginmedia.com", "ntlworld.com", "blueyonder.co.uk",
  "talktalk.net", "aol.com", "aol.co.uk", "protonmail.com", "proton.me",
];

// Edit distance counting a swap of neighbours as one slip (gmial -> gmail).
function distance(a, b) {
  const d = Array.from({ length: a.length + 1 }, (_, i) => [i, ...Array(b.length).fill(0)]);
  for (let j = 1; j <= b.length; j++) d[0][j] = j;
  for (let i = 1; i <= a.length; i++) {
    for (let j = 1; j <= b.length; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      d[i][j] = Math.min(d[i - 1][j] + 1, d[i][j - 1] + 1, d[i - 1][j - 1] + cost);
      if (i > 1 && j > 1 && a[i - 1] === b[j - 2] && a[i - 2] === b[j - 1]) d[i][j] = Math.min(d[i][j], d[i - 2][j - 2] + 1);
    }
  }
  return d[a.length][b.length];
}

// The corrected address, or null if it looks fine (or isn't close enough
// to a common provider to guess).
export function suggestEmail(email) {
  const value = String(email || "").trim();
  const at = value.lastIndexOf("@");
  if (at < 1 || at === value.length - 1) return null;
  const local = value.slice(0, at);
  const domain = value.slice(at + 1).toLowerCase();
  if (COMMON_EMAIL_DOMAINS.includes(domain)) return null;

  // Endings people slip on, whatever comes before: .con, .cmo, .co.ukk.
  const fixedEnding = domain
    .replace(/\.(con|cpm|cmo|ocm|vom|xom|comm|om)$/, ".com")
    .replace(/\.(co\.ukk|co\.uj|co\.k|cp\.uk|co\.ul|couk|co,uk|co\.uk\.)$/, ".co.uk")
    .replace(/\.(nett|nte|ent)$/, ".net");

  let best = null;
  let bestScore = Infinity;
  for (const candidate of COMMON_EMAIL_DOMAINS) {
    const score = distance(fixedEnding, candidate);
    if (score < bestScore) { best = candidate; bestScore = score; }
  }
  // Close to a common provider: suggest it. Short endings allow only one
  // slip, or one real provider would be "corrected" to another (bt.com is
  // two slips from me.com). Otherwise, if only the ending was wrong
  // (someone's own domain), suggest that alone.
  const allowed = fixedEnding.length < 9 ? 1 : 2;
  if (best && bestScore > 0 && bestScore <= allowed) return `${local}@${best}`;
  if (bestScore === 0) return `${local}@${best}`;
  if (fixedEnding !== domain) return `${local}@${fixedEnding}`;
  return null;
}
